import { Command } from 'cmdk';
import { useEffect, useState } from 'react';
import { useStore } from '../store.js';

/**
 * ⌘K / Ctrl-K opens a palette of in-app actions. Minimal: view-switching,
 * canonicalise, clear. Expand as workflows demand.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const setView = useStore((s) => s.setView);
  const canonicalise = useStore((s) => s.canonicalise);
  const clear = useStore((s) => s.clear);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (!open) return null;

  const run = (action: () => void) => {
    action();
    setOpen(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-ink-950/70 p-4 pt-[15vh] backdrop-blur-sm"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-xl rounded-lg border border-ink-800 bg-ink-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <Command className="flex flex-col">
          <Command.Input
            placeholder="Type a command…"
            className="mono w-full border-b border-ink-800 bg-transparent px-4 py-3 text-[13px] text-ink-100 placeholder:text-ink-600 outline-none"
            autoFocus
          />
          <Command.List className="max-h-[50vh] overflow-auto p-2">
            <Command.Empty className="px-2 py-4 text-center text-[12px] text-ink-500">
              No commands match.
            </Command.Empty>
            <Command.Group heading="View" className="label px-2 py-1">
              <Item onSelect={() => run(() => setView('dashboard'))}>Go to Dashboard</Item>
              <Item onSelect={() => run(() => setView('editor'))}>Go to Editor</Item>
              <Item onSelect={() => run(() => setView('constellation'))}>Go to Constellation</Item>
              <Item onSelect={() => run(() => setView('lint'))}>Go to Lint</Item>
              <Item onSelect={() => run(() => setView('build'))}>Go to Build</Item>
            </Command.Group>
            <Command.Group heading="Doc" className="label px-2 py-1">
              <Item onSelect={() => run(canonicalise)}>Canonicalise</Item>
              <Item onSelect={() => run(clear)}>Close file</Item>
            </Command.Group>
          </Command.List>
        </Command>
      </div>
    </div>
  );
}

function Item({
  children,
  onSelect,
}: {
  children: React.ReactNode;
  onSelect: () => void;
}) {
  return (
    <Command.Item
      onSelect={onSelect}
      className="mono flex cursor-pointer items-center rounded px-3 py-2 text-[13px] text-ink-200 aria-selected:bg-ink-800 aria-selected:text-accent-500"
    >
      {children}
    </Command.Item>
  );
}
